/**
 * stores/chats.js — chat list (per-(contact, channel) rows from CRM + proxy).
 *
 * #153 — chat rows are now keyed by a composite `${contactId}:${channelId}`
 * so a single CRM contact messaging on multiple WhatsApp channels appears
 * as multiple rows, each with its own last-message preview, unread count,
 * and color accent. Tags/CF/assignment remain contact-scoped (the CRM has
 * a single contact record per phone) and are fanned out by the proxy.
 */

import { map, atom, computed } from 'nanostores';
import { $openChatKey } from './ui.js';

// ── Composite key helpers ───────────────────────────────────────────────────
//
// A chat_key has the shape `${contactId}:${channelId}`. For orphan rows
// (contacts with no inbox_messages history yet), we fall back to just the
// contactId so the row still has a stable identifier. The parser tolerates
// both shapes.

export function makeChatKey(contactId, channelId) {
  if (!contactId) return null;
  return channelId ? `${contactId}:${channelId}` : String(contactId);
}

export function parseChatKey(key) {
  if (!key) return { contactId: null, channelId: null };
  const i = String(key).indexOf(':');
  if (i < 0) return { contactId: String(key), channelId: null };
  return { contactId: key.slice(0, i), channelId: key.slice(i + 1) };
}

// Map of chat_key → chat object. Shape per row:
//   { id (= chat_key), contactId, name, phone, channelPhoneId, agent,
//     lastSeen, unread, tags, lastMessage, lastMessageTs, lastInboundTs }
export const $chats = map({});

// T14 — two-layer account-unread model.
//
// Pre-T14: a single $accountUnread atom was mutated from 4 independent
// sites (chats-service, realtime +1, markUnreadAsRead local decrement,
// mark-read T11 server-sync). They repeatedly drifted (T11, T12 fixed
// symptoms but not the class).
//
// Post-T14: $accountUnread is DERIVED. The only store mutated by app
// code is $chats (individual chat.unread values) plus $serverAccountUnread
// which holds whatever the server last told us across ALL chats
// (including those paginated out of our local $chats map).
//
// The derived value is max(local_sum, server_count):
//   - Local sum reacts INSTANTLY to realtime inserts (chat.unread +1 on
//     line ~605 of realtime.js flows automatically) and to mark-read
//     (updateChat unread=0 decrements the sum).
//   - Server count acts as a floor for paginated-out chats. A brand-new
//     inbound on chat #51 bumps the server count via FCM push +
//     /chats refresh, and the floor keeps us accurate even though our
//     local sum would miss it.
//   - max() means we never undercount and, when the server is stale
//     between refreshes, local sum takes over with faster reactivity.
//
// CRITICAL: do not import $accountUnread anywhere that wants to WRITE.
// nanostores' computed is read-only; writes throw. Code that previously
// wrote to $accountUnread now writes to $serverAccountUnread (if the
// value is authoritative from the server) or to $chats[k].unread via
// updateChat() (if the write represents per-chat state).
export const $serverAccountUnread = atom(0);

export const $accountUnread = computed(
  [$chats, $serverAccountUnread],
  (chats, serverCount) => {
    let localSum = 0;
    for (const k in chats) localSum += (chats[k]?.unread || 0);
    const srv = Number.isFinite(serverCount) ? serverCount : 0;
    return Math.max(localSum, srv);
  }
);

// (phone, channelId) → chat_key lookup index. Realtime uses this to route
// an incoming inbox_messages row to the correct per-channel chat row in O(1).
// Orphan rows (channelId missing) are also indexed under `${phone}:` so a
// fallback lookup still works until the first message arrives.
export const $phoneIndex = computed($chats, (chats) => {
  const idx = {};
  for (const id in chats) {
    const c = chats[id];
    if (!c?.phone) continue;
    const key = `${c.phone}:${c.channelPhoneId || ''}`;
    idx[key] = id;
  }
  return idx;
});

// Derived: currently open chat — lookups use the composite $openChatKey.
export const $currentChat = computed(
  [$chats, $openChatKey],
  (chats, key) => key ? (chats[key] || null) : null
);

// Order of chat_keys (sorted by last activity). Maintained by setChatList /
// addChat / bumpChat. Rendering reads this to keep the list stable.
export const $chatOrder = atom([]);

// Current filter: 'all' | 'mine' | 'unread' | 'unassigned'
export const $chatFilter = atom('all');

// Channel filter — Partner Panel channel_id (stored under the historical
// "channelPhoneId" name — see getChannelInfo in services/channel-colors.js)
// or the literal string 'all'. Persisted per-tab in localStorage so the
// last-used filter survives reloads.
const _CHANNEL_FILTER_KEY = 'inbox_v2_channel_filter';
export const $channelFilter = atom(localStorage.getItem(_CHANNEL_FILTER_KEY) || 'all');
$channelFilter.listen((v) => {
  try {
    if (v && v !== 'all') localStorage.setItem(_CHANNEL_FILTER_KEY, v);
    else                  localStorage.removeItem(_CHANNEL_FILTER_KEY);
  } catch {}
});

// Search query
export const $chatSearch = atom('');

// ── Derived: filtered chat list ──────────────────────────────────────────────

export const $filteredChats = computed(
  [$chats, $chatOrder, $chatFilter, $channelFilter, $chatSearch],
  (chats, order, filter, channelFilter, search) => {
    let result = order.map(id => chats[id]).filter(Boolean);

    if (filter === 'unread')     result = result.filter(c => (c.unread || 0) > 0);
    if (filter === 'unassigned') result = result.filter(c => !c.agent);
    // 'mine' filter handled at API level (passes ?agent=email)

    if (channelFilter !== 'all') {
      // #153: each row is already channel-scoped, so a simple equality
      // check does the job. Rows with no channelPhoneId (orphan contacts
      // with no inbox_messages history) only appear under "All".
      result = result.filter(c => c.channelPhoneId === channelFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q)
      );
    }

    return result;
  }
);

// ── Actions ──────────────────────────────────────────────────────────────────

/** Replace the entire chat list (after fresh load from CRM) */
export function setChatList(chatArray) {
  const newMap = {};
  const newOrder = [];
  for (const c of chatArray) {
    newMap[c.id] = c;
    newOrder.push(c.id);
  }
  $chats.set(newMap);
  $chatOrder.set(newOrder);
}

/** Update a single chat (e.g., new message arrived → bump preview + last_seen) */
export function updateChat(chatKey, partial) {
  const current = $chats.get()[chatKey];
  if (!current) return;
  $chats.setKey(chatKey, { ...current, ...partial });
}

/**
 * Update every row in the store that shares the given CRM contact id. Used
 * by contact-scoped CRM events (assign, tag, CF) so both of Sambit's rows
 * reflect a tag change or agent reassignment simultaneously. Parent passes
 * a partial that is shared across all matching rows.
 */
export function updateChatByContactId(contactId, partial) {
  if (!contactId) return;
  const next = { ...$chats.get() };
  let touched = false;
  for (const key in next) {
    if (next[key]?.contactId === contactId) {
      next[key] = { ...next[key], ...partial };
      touched = true;
    }
  }
  if (touched) $chats.set(next);
}

/** Move a chat to the top of the list (e.g., new message) */
export function bumpChat(chatKey) {
  const order = $chatOrder.get();
  if (!order.includes(chatKey)) return;
  $chatOrder.set([chatKey, ...order.filter(id => id !== chatKey)]);
}

/** Add a new chat (new contact started messaging on a new channel) */
export function addChat(chat) {
  $chats.setKey(chat.id, chat);
  $chatOrder.set([chat.id, ...$chatOrder.get().filter(id => id !== chat.id)]);
}

/** Remove a chat (deleted contact) */
export function removeChat(chatKey) {
  const next = { ...$chats.get() };
  delete next[chatKey];
  $chats.set(next);
  $chatOrder.set($chatOrder.get().filter(id => id !== chatKey));
}
